/**
 * Calendar view preferences, persisted to localStorage.
 *
 *   - showMine    — scope tasks + planned contacts to the current user
 *                   (default on, like the tasks + planned-contacts
 *                   workspaces). Events, deliveries and deadlines are
 *                   company-wide and unchanged by it.
 *   - showTasks   — render open tasks (default on).
 *   - showPlans   — render planned contacts (default on; needs kontakty read).
 *   - showTerminy — render used-car STK / servisní prohlídka deadlines
 *                   (default on; needs ojeta_vozidla read).
 */

import { useCallback, useState } from 'react';

export interface KalendarPrefs {
  showMine: boolean;
  showTasks: boolean;
  showPlans: boolean;
  showTerminy: boolean;
}

const STORAGE_KEY = 'kalendar-prefs';
const DEFAULT_PREFS: KalendarPrefs = {
  showMine: true,
  showTasks: true,
  showPlans: true,
  showTerminy: true,
};

function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function readPrefs(): KalendarPrefs {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<KalendarPrefs>;
    return {
      showMine: bool(parsed.showMine, DEFAULT_PREFS.showMine),
      showTasks: bool(parsed.showTasks, DEFAULT_PREFS.showTasks),
      showPlans: bool(parsed.showPlans, DEFAULT_PREFS.showPlans),
      showTerminy: bool(parsed.showTerminy, DEFAULT_PREFS.showTerminy),
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function useKalendarPrefs(): [KalendarPrefs, (patch: Partial<KalendarPrefs>) => void] {
  const [prefs, setPrefs] = useState<KalendarPrefs>(readPrefs);
  const update = useCallback((patch: Partial<KalendarPrefs>) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore quota / disabled storage
      }
      return next;
    });
  }, []);
  return [prefs, update];
}
